"use client";

import { ArrowDown2 } from "iconsax-reactjs";
import { Check } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { XIcon } from "@/components/ui/XIcon";
import { $api } from "@/lib/api-client";
import { localizeName } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { ColorSwatch } from "./ColorSwatch";

// ----------------------------------------------------------------------

interface ColorOption {
  id: string;
  name: { en?: string | null; fa?: string | null };
  code: string;
}

interface ColorSelectProps {
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  multiple?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const ColorSelect = ({
  value,
  onChange,
  multiple = false,
  placeholder,
  disabled = false,
  className,
}: ColorSelectProps) => {
  const t = useTranslations("modals");
  const locale = useLocale();
  const resolvedPlaceholder = placeholder ?? t("colorSelect.selectColor");
  const { data, isLoading } = $api.useQuery("get", "/color");
  const colors = (data?.data || []) as ColorOption[];

  const getColorName = (color: ColorOption) => {
    return localizeName(color.name, locale);
  };

  const getColorById = (id: string) => {
    return colors.find((c) => c.id === id);
  };

  // Multiple selection mode
  if (multiple) {
    const selectedIds = (value as string[]) || [];
    const selectedColors = selectedIds
      .map((id) => getColorById(id))
      .filter(Boolean) as ColorOption[];

    const handleToggle = (colorId: string) => {
      const isSelected = selectedIds.includes(colorId);
      let newValue: string[];

      if (isSelected) {
        newValue = selectedIds.filter((id) => id !== colorId);
      } else {
        newValue = [...selectedIds, colorId];
      }

      onChange?.(newValue);
    };

    const handleRemove = (e: React.MouseEvent, colorId: string) => {
      e.stopPropagation();
      const newValue = selectedIds.filter((id) => id !== colorId);
      onChange?.(newValue);
    };

    return (
      <div className={cn("space-y-2 w-full", className)}>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="bordered"
              colors="secondary"
              disabled={disabled || isLoading}
              radius="xs"
              className={cn(
                "w-full justify-between h-9 border-stroke-light bg-transparent px-2",
                !selectedColors.length && "text-muted-foreground",
              )}
            >
              {isLoading ? (
                <Spinner className="h-4 w-4 text-text-secondary ms-auto" />
              ) : (
                <>
                  <span className="truncate line-clamp-1 text-muted-foreground">
                    {selectedColors.length > 0
                      ? t("colorSelect.colorsSelected", {
                          count: String(selectedColors.length),
                        })
                      : resolvedPlaceholder}
                  </span>
                  <ArrowDown2 size={16} className="text-primary" />
                </>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-(--radix-popover-trigger-width) p-0 bg-bg-secondary rounded-md border-stroke-subtle shadow-[0px_2px_4px_-2px_#0000001A,0px_4px_6px_-1px_#0000001A]"
            align="end"
          >
            <div className="max-h-60 overflow-auto p-2">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner className="h-5 w-5 text-text-secondary" />
                </div>
              ) : colors.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <span className="text-sm text-text-secondary">
                    {t("colorSelect.noColors")}
                  </span>
                </div>
              ) : (
                colors.map((color) => {
                  const isSelected = selectedIds.includes(color.id);
                  return (
                    <div
                      key={color.id}
                      className={cn(
                        "flex items-center gap-2 rounded-xs px-2 py-1.5 hover:bg-bg-tertiary/60 cursor-pointer",
                      )}
                      onClick={() => handleToggle(color.id)}
                    >
                      {isSelected ? (
                        <Check className="h-4 w-4 text-primary" />
                      ) : (
                        <span className="h-4 w-4" />
                      )}
                      <ColorSwatch code={color.code} className="size-6" />
                      <span className="text-sm font-medium flex-1">
                        {getColorName(color)}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </PopoverContent>
        </Popover>

        {selectedColors.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selectedColors.map((color) => (
              <Badge
                key={color.id}
                variant="outline"
                size="sm"
                className="gap-1.5 pr-1 pl-1 py-0.5 text-xs bg-transparent"
              >
                <ColorSwatch code={color.code} className="size-4" />
                <span className="truncate max-w-[100px] mt-0.5">
                  {getColorName(color)}
                </span>
                <button
                  type="button"
                  className="flex items-center justify-center hover:bg-bg-tertiary rounded-xs p-0.5 transition-colors"
                  onClick={(e) => handleRemove(e, color.id)}
                >
                  <XIcon size={16} className="text-text-secondary" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Single selection mode
  const selectedColor = getColorById(value as string);

  return (
    <div className={cn("w-full", className)}>
      <Select
        value={(value as string) || ""}
        onValueChange={(val) => onChange?.(val)}
        disabled={disabled || isLoading}
      >
        <SelectTrigger className="w-full">
          {isLoading ? (
            <Spinner className="h-4 w-4 text-text-secondary" />
          ) : selectedColor ? (
            <div className="flex items-center gap-2">
              <ColorSwatch code={selectedColor.code} className="size-5" />
              <span>{getColorName(selectedColor)}</span>
            </div>
          ) : (
            <SelectValue placeholder={resolvedPlaceholder} />
          )}
        </SelectTrigger>
        <SelectContent className="bg-bg-secondary rounded-sm border-stroke-subtle shadow-[0px_2px_4px_-2px_#0000001A,0px_4px_6px_-1px_#0000001A]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner className="h-5 w-5 text-text-secondary" />
            </div>
          ) : (
            colors.map((color) => (
              <SelectItem
                key={color.id}
                value={color.id}
                className="font-medium rounded-sm"
              >
                <div className="flex items-center gap-2">
                  <ColorSwatch code={color.code} className="size-5" />
                  <span>{getColorName(color)}</span>
                </div>
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </div>
  );
};
